import type { ReactNode } from 'react';
import { useState } from 'react';
import type { InternshipProcessWithNames } from '../../Types/interfaces';
import { StudyLineType } from '../../Types/interfaces';
import { EvaluationComponentForComputerScience } from './EvaluationComponentForComputerScience';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EvaluationComponentForMathAndMathComputerScience } from './EvaluationComponentForMathAndMathComputerScience';
import { Button, Card, Col, Row, Typography } from 'antd';
import { t } from 'i18next';

interface EvaluationChooserComponentProps {
  process: InternshipProcessWithNames;
}

export const EvaluationChooserComponent = ({
  process,
}: EvaluationChooserComponentProps) => {
  const [componentType, setComponentType] = useState<StudyLineType>(
    StudyLineType.MathComputerScience
  );

  const queryClient = useQueryClient();

  const GetCorrectEvaluationType = (): ReactNode => {
    switch (componentType) {
      case StudyLineType.ComputerScience:
        return (
          <>
            <EvaluationComponentForComputerScience
              process={process}
              evaluationFinished={async () => {
                queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
                });
              }}
            />

            <Card style={{ maxWidth: 400, margin: 30 }}>
              <Button
                block
                type="default"
                onClick={() => {
                  setComponentType(StudyLineType.MathComputerScience);
                }}
              >
                {t('evaluationComp.chooser.repick')}
              </Button>
            </Card>
          </>
        );

      case StudyLineType.Math:
        return (
          <>
            <EvaluationComponentForMathAndMathComputerScience
              process={process}
              evaluationFinished={() => {
                queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
                });
                queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.EVALUATED_PROCESSES,
                });
              }}
            />
            <Card style={{ maxWidth: 400, margin: 30 }}>
              <Button
                block
                type="default"
                onClick={() => {
                  setComponentType(StudyLineType.MathComputerScience);
                }}
              >
                {t('evaluationComp.chooser.repick')}
              </Button>
            </Card>
          </>
        );

      case StudyLineType.MathComputerScience:
        return (
          <Card
            style={{
              maxWidth: 700,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            <Typography.Title level={4}>
              {t('evaluationComp.chooser.info')}
            </Typography.Title>

            <Typography.Paragraph type="secondary">
              {t('evaluationComp.chooser.description')}
            </Typography.Paragraph>

            <Row justify="center" gutter={[24, 24]} style={{ marginTop: 32 }}>
              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() =>
                    setComponentType(StudyLineType.ComputerScience)
                  }
                >
                  {t('evaluationComp.chooser.computerScience')}
                </Button>
              </Col>

              <Col xs={24} sm={12}>
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={() => setComponentType(StudyLineType.Math)}
                >
                  {t('evaluationComp.chooser.math')}
                </Button>
              </Col>
            </Row>
          </Card>
        );
    }
  };

  return GetCorrectEvaluationType();
};
